import { Injectable } from '@angular/core';
import { AlertService } from './alert.service';
import { LoadingService } from './loading.service';
import { Platform } from '@ionic/angular';
import { LogService } from './logger/log.service';
import { stringifySafety } from '@core/services/system/logger';
import { PlatformType } from '@shared/constants/platform-types.enum';
import { Keyboard, SplashScreen, StatusBar, Camera } from '@core/capacitor-plugins/capacitor-plugins.class';
import { TimApiPlugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { MyPosMiniPlugin } from '@features/my-pos-mini';
import { MyPosN5Plugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { SunmiPrinterPlugin, SunmiScannerPlugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { SumupPlugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { Database } from '@paymash/capacitor-database-plugin';
import { StarPrinterPlugin } from '@paymash/capacitor-star-printer-plugin';
import { EpsonprintPlugin } from '@paymash/capacitor-epsonprint-plugin';
import { SocketScannerPlugin } from '@core/capacitor-plugins/capacitor-plugins.class';
import { WifiInfoPlugin } from '@paymash/capacitor-wifi-info-plugin';
import { PrintPdfPlugin } from '@paymash/capacitor-print-pdf-plugin';
// NOTE: ScannerAppPlugin is iOS-only. Importing it on Android triggers
// `CapacitorException: "ScannerAppPlugin" plugin is not implemented on android`
// because the plugin's base class registers a native listener at construction
// time. We use `Capacitor.isPluginAvailable` to detect availability instead.
import { Capacitor } from '@capacitor/core';
import { BarcodeScannerPlugin } from '@paymash/capacitor-barcode-scanner-plugin';
import { FingerprintPlugin } from '@paymash/capacitor-fingerprint-plugin';
import { TargetDeviceEnum } from '@shared/constants';

interface IPluginItem {
  name: string;
  selector: any;
  platform?: PlatformType;
}

const matrixTargetDeviceToPluginPlatform: Partial<Record<TargetDeviceEnum, PlatformType>> = {
  [TargetDeviceEnum.ANDROID]: PlatformType.Android,
  [TargetDeviceEnum.ADYEN]: PlatformType.Adyen,
  [TargetDeviceEnum.SUNMI]: PlatformType.Sunmi,
  [TargetDeviceEnum.MYPOS]: PlatformType.Mypos,
};

// tslint:disable: no-string-literal
@Injectable()
export class PosSelfCheckService {
  private readonly logger = this.logService.createLogger('PosSelfCheckService');
  private get androidType(): PlatformType {
    const targetDevice: string = localStorage.getItem('targetDevice');
    return matrixTargetDeviceToPluginPlatform[targetDevice] ?? PlatformType.Android;
  }
  constructor(
    private alertService: AlertService,
    private loadingService: LoadingService,
    private platform: Platform,
    private logService: LogService
  ) {}

  public checkPluginsState() {
    if (!window.cordova) {
      return;
    }
    this.loadingService.showLoadingItem(0, 'POS self check', true);
    const platformType: PlatformType = this.platform.is(PlatformType.iOS) ? PlatformType.iOS : this.androidType;
    try {
      const pluginsList = this.getNotInstalledPlugins(platformType);
      if (pluginsList.length > 0) {
        this.showMissedPluginsNotification(pluginsList.join('\n'));
      }
    } catch (e) {
      this.logger.error(e, 'checkPluginsState');
      this.showMissedPluginsNotification(stringifySafety(e));
    }
    this.loadingService.hideLoadingItem();
  }

  private async showMissedPluginsNotification(message: string) {
    const alert = await this.alertService.create({
      header: 'Error',
      message: message,
      buttons: [{ text: 'OK' }],
    });
    alert.present().catch((error) => this.logger.error(error, 'showMissedPluginsNotification'));
  }

  private getNotInstalledPlugins(platformType: PlatformType): string[] {
    const pluginList: IPluginItem[] = this.getPluginList();
    const filteredPluginList: IPluginItem[] = pluginList.filter((plugin) => !plugin.platform || plugin.platform === platformType);
    return filteredPluginList.filter((plugin: IPluginItem) => !plugin.selector).map((item) => item.name);
  }

  private getPluginList() {
    const iOSPlaginList: IPluginItem[] = this.getiOSPluginsList();
    const androidPlaginList: IPluginItem[] = this.getAndroidPluginsList();
    const commonPluginList: IPluginItem[] = this.getCommonPluginList();
    const adyenPluginList: IPluginItem[] = this.getAdyenPluginList();
    const sunmiPluginList: IPluginItem[] = this.getSunmiPluginList();
    const myPOSPluginList: IPluginItem[] = this.getMyPOSPluginList();
    return [...iOSPlaginList, ...androidPlaginList, ...commonPluginList, ...adyenPluginList, ...sunmiPluginList, ...myPOSPluginList];
  }

  private getiOSPluginsList(): IPluginItem[] {
    return [
      {
        name: 'com.fabware.ScannerAppPlugin',
        platform: PlatformType.iOS,
        selector: Capacitor.isPluginAvailable('ScannerAppPlugin') ? {} : null,
      },
      {
        name: 'fabware-tim-api-plugin',
        platform: PlatformType.iOS,
        selector: TimApiPlugin,
      },
      {
        name: 'com.fabware.MyPosMiniPlugin',
        platform: PlatformType.iOS,
        selector: MyPosMiniPlugin,
      },
    ];
  }

  private getAndroidPluginsList(): IPluginItem[] {
    return [
      {
        name: 'fabware-tim-api-plugin',
        platform: PlatformType.Android,
        selector: TimApiPlugin,
      },
      {
        name: 'cordova-plugin-android-permissions',
        platform: PlatformType.Android,
        selector: cordova.plugins['permissions'],
      },
      {
        name: 'com.fabware.MyPosMiniPlugin',
        platform: PlatformType.Android,
        selector: MyPosMiniPlugin,
      },
    ];
  }

  private getCommonPluginList(): IPluginItem[] {
    const pluginsList: IPluginItem[] = [
      {
        name: 'com.fabware.SocketScannerPlugin',
        selector: SocketScannerPlugin,
      },
      {
        name: 'com.fabware.SumupPlugin',
        selector: SumupPlugin,
      },
      {
        name: 'com.fabware.WifiDataPlugin',
        selector: WifiInfoPlugin,
      },
      {
        name: 'com-nyxmo-plugins-ecr',
        selector: ecr,
      },
      {
        name: 'cordova-plugin-print-pdf',
        selector: PrintPdfPlugin,
      },
      {
        name: 'cordova-plugin-android-permissions',
        selector: cordova.plugins['permissions'],
      },
      {
        name: 'capacitor-plugin-camera',
        selector: Camera,
      },
      {
        name: 'cordova-plugin-console',
        selector: console,
      },
      {
        name: 'cordova-plugin-inappbrowser',
        selector: cordova['InAppBrowser'],
      },
      {
        name: 'capacitor-plugin-keyboard',
        selector: Keyboard,
      },
      {
        name: 'cordova-plugin-nativestorage',
        selector: window['NativeStorage'],
      },
      {
        name: 'capacitor-plugin-splashscreen',
        selector: SplashScreen,
      },
      {
        name: 'capacitor-plugin-statusbar',
        selector: StatusBar,
      },
      {
        name: 'com.paymash.DatabasePlugin',
        selector: Database,
      },
      {
        name: 'com.paymash.StarPrinter',
        selector: StarPrinterPlugin,
      },
      {
        name: 'com.paymash.Epsonprint',
        selector: EpsonprintPlugin,
      },
      {
        name: 'com-fabware-barcode-scanner',
        selector: BarcodeScannerPlugin,
      },
      {
        name: 'fingerprint-plugin',
        selector: FingerprintPlugin,
      },
    ];
    return pluginsList;
  }

  private getAdyenPluginList(): IPluginItem[] {
    return [
      {
        name: 'cordova-plugin-android-permissions',
        platform: PlatformType.Adyen,
        selector: cordova.plugins['permissions'],
      }
    ];
  }

  private getSunmiPluginList(): IPluginItem[] {
    return [
      {
        name: 'capacitor-sunmi-plugin',
        platform: PlatformType.Sunmi,
        selector: SunmiPrinterPlugin,
      },
      {
        name: 'capacitor-sunmi-plugin',
        platform: PlatformType.Sunmi,
        selector: SunmiScannerPlugin,
      },
    ];
  }

  private getMyPOSPluginList(): IPluginItem[] {
    return [
      {
        name: 'com.fabware.MyPosN5Plugin',
        platform: PlatformType.Mypos,
        selector: MyPosN5Plugin,
      },
    ];
  }
}
// tslint:enable: no-string-literal
